<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';

import { Temporal } from '@shared/lib/date-time';

import { IconChevronDown, IconChevronLeft, IconChevronRight } from '../icons';

const props = withDefaults(
  defineProps<{
    modelValue: Temporal.PlainDate | null;
    today: Temporal.PlainDate;
    /** Дни вне диапазона `min`…`max` выбрать нельзя. */
    min?: Temporal.PlainDate;
    max?: Temporal.PlainDate;
    /** Подсветка периода: концы и дни между ними. Месяц не прыгает, пока виден хотя бы один конец. */
    range?: { from: Temporal.PlainDate | null; to: Temporal.PlainDate | null };
    /** Без своей рамки и отступов, для вставки в чужое окно. */
    embedded?: boolean;
    /** Месяцев рядом; выбранная дата всегда в последнем, перед ним прошлые. */
    months?: 1 | 2;
  }>(),
  { min: undefined, max: undefined, range: undefined, months: 1 },
);
const emit = defineEmits<{
  'update:modelValue': [value: Temporal.PlainDate];
  close: [];
  /** Выбран месяц в сетке месяцев; первый день месяца. */
  month: [first: Temporal.PlainDate];
}>();

const sameMonth = (a: Temporal.PlainDate, b: Temporal.PlainDate): boolean => a.year === b.year && a.month === b.month;
const firstShown = (anchor: Temporal.PlainDate) => anchor.with({ day: 1 }).subtract({ months: props.months - 1 });

const firstMonth = shallowRef(firstShown(props.modelValue ?? props.range?.to ?? props.range?.from ?? props.today));
const visibleMonths = computed(() =>
  Array.from({ length: props.months }, (_, i) => firstMonth.value.add({ months: i })),
);
const isVisible = (day: Temporal.PlainDate | null | undefined): boolean =>
  Boolean(day && visibleMonths.value.some((month) => sameMonth(month, day)));

watch(
  () => props.range,
  (range) => {
    const target = range?.to ?? range?.from;
    if (target && !isVisible(range?.from) && !isVisible(range?.to)) firstMonth.value = firstShown(target);
  },
);
watch(
  () => props.modelValue,
  (value) => {
    if (value && !isVisible(value)) firstMonth.value = firstShown(value);
  },
);
// Последний месяц остаётся на месте, меняется число прошлых перед ним.
watch(
  () => props.months,
  (count, previous) => {
    firstMonth.value = firstMonth.value.add({ months: previous - count });
  },
);

const monthLabel = (month: Temporal.PlainDate) => `${month.toLocaleString('ru-RU', { month: 'long' })} ${month.year}`;
const monthDays = (month: Temporal.PlainDate) =>
  Array.from({ length: month.daysInMonth }, (_, i) => month.add({ days: i }));
const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const shift = (months: number) => {
  firstMonth.value = firstMonth.value.add({ months });
};

const pickingMonth = shallowRef(false);
const gridYear = shallowRef(props.today.year);
const yearMonths = computed(() =>
  Array.from({ length: 12 }, (_, i) => Temporal.PlainDate.from({ year: gridYear.value, month: i + 1, day: 1 })),
);
const toggleMonthGrid = () => {
  gridYear.value = firstMonth.value.add({ months: props.months - 1 }).year;
  pickingMonth.value = !pickingMonth.value;
};
const pickMonth = (first: Temporal.PlainDate) => {
  firstMonth.value = firstShown(first);
  pickingMonth.value = false;
  emit('month', first);
};
const isMonthPicked = (first: Temporal.PlainDate) => {
  if (!props.range) return props.modelValue ? sameMonth(props.modelValue, first) : false;
  const { from, to } = props.range;
  return Boolean(from?.equals(first) && to && sameMonth(to, first));
};
const isMonthOutOfRange = (first: Temporal.PlainDate) =>
  (props.max !== undefined && Temporal.PlainDate.compare(first, props.max) > 0) ||
  (props.min !== undefined && Temporal.PlainDate.compare(first.with({ day: first.daysInMonth }), props.min) < 0);

const selectDate = (day: Temporal.PlainDate) => {
  emit('update:modelValue', day);
  emit('close');
};

// Пока выбран только начало периода, наведение показывает будущий период.
const hoverDay = shallowRef<Temporal.PlainDate | null>(null);
const band = computed(() => {
  const { from, to } = props.range ?? {};
  if (!from) return null;
  if (to) return { from, to, preview: false };
  const hovered = hoverDay.value;
  if (!hovered || hovered.equals(from)) return null;
  return Temporal.PlainDate.compare(hovered, from) < 0
    ? { from: hovered, to: from, preview: true }
    : { from, to: hovered, preview: true };
});

const isSelected = (day: Temporal.PlainDate) =>
  (props.modelValue?.equals(day) ?? false) ||
  (props.range?.from?.equals(day) ?? false) ||
  (props.range?.to?.equals(day) ?? false);
const isOutOfRange = (day: Temporal.PlainDate) =>
  (props.min !== undefined && Temporal.PlainDate.compare(day, props.min) < 0) ||
  (props.max !== undefined && Temporal.PlainDate.compare(day, props.max) > 0);

const dayClass = (day: Temporal.PlainDate) => {
  const current = band.value;
  const isStart = current?.from.equals(day) ?? false;
  const isEnd = current?.to.equals(day) ?? false;
  const inside =
    current !== null &&
    Temporal.PlainDate.compare(day, current.from) > 0 &&
    Temporal.PlainDate.compare(day, current.to) < 0;
  // Полоса рвётся на краях недели и месяца: там её концы скругляются.
  const rowStart = day.dayOfWeek === 1 || day.day === 1;
  const rowEnd = day.dayOfWeek === 7 || day.day === day.daysInMonth;

  return {
    'date-picker-day-today': day.equals(props.today),
    'date-picker-day-selected': isSelected(day),
    'date-picker-day-hovered': current?.preview === true && hoverDay.value?.equals(day) === true,
    'date-picker-day-in-range': inside,
    'date-picker-band-start': isStart && !isEnd && !rowEnd,
    'date-picker-band-end': isEnd && !isStart && !rowStart,
    'date-picker-band-preview': current?.preview === true,
    'rounded-l-full': inside && rowStart,
    'rounded-r-full': inside && rowEnd,
  };
};

const hover = (day: Temporal.PlainDate) => {
  if (props.range?.from && !props.range.to) hoverDay.value = day;
};
</script>

<template>
  <div
    class="date-picker"
    :class="{ 'date-picker-embedded': embedded }"
  >
    <template v-if="pickingMonth">
      <div class="date-picker-header">
        <button
          type="button"
          class="date-picker-nav-button"
          aria-label="Предыдущий год"
          @click="gridYear--"
        >
          <IconChevronLeft class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="date-picker-month"
          aria-expanded="true"
          @click="toggleMonthGrid"
        >
          {{ gridYear }}
          <IconChevronDown
            class="rotate-180 text-(--text-secondary)"
            :width="10"
            :height="10"
          />
        </button>
        <button
          type="button"
          class="date-picker-nav-button"
          aria-label="Следующий год"
          @click="gridYear++"
        >
          <IconChevronRight class="h-4 w-4" />
        </button>
      </div>
      <div
        class="date-picker-month-grid"
        :class="months === 2 ? 'grid-cols-4' : 'grid-cols-3'"
      >
        <button
          v-for="month in yearMonths"
          :key="month.month"
          type="button"
          class="date-picker-month-option"
          :class="{
            'date-picker-day-today': sameMonth(month, today),
            'date-picker-month-option-picked': isMonthPicked(month),
          }"
          :disabled="isMonthOutOfRange(month)"
          :aria-pressed="isMonthPicked(month)"
          @click="pickMonth(month)"
        >
          {{ month.toLocaleString('ru-RU', { month: 'long' }) }}
        </button>
      </div>
    </template>

    <div
      v-else
      class="date-picker-panes"
      :class="{ 'grid-cols-2': months === 2 }"
    >
      <div
        v-for="(month, index) in visibleMonths"
        :key="month.toString()"
      >
        <div class="date-picker-header">
          <button
            v-if="index === 0"
            type="button"
            class="date-picker-nav-button"
            aria-label="Предыдущий месяц"
            @click="shift(-1)"
          >
            <IconChevronLeft class="h-4 w-4" />
          </button>
          <span
            v-else
            class="size-8"
          />
          <button
            type="button"
            class="date-picker-month"
            aria-expanded="false"
            @click="toggleMonthGrid"
          >
            {{ monthLabel(month) }}
            <IconChevronDown
              class="text-(--text-secondary)"
              :width="10"
              :height="10"
            />
          </button>
          <button
            v-if="index === visibleMonths.length - 1"
            type="button"
            class="date-picker-nav-button"
            aria-label="Следующий месяц"
            @click="shift(1)"
          >
            <IconChevronRight class="h-4 w-4" />
          </button>
          <span
            v-else
            class="size-8"
          />
        </div>
        <div class="date-picker-weekdays">
          <div
            v-for="day in weekDays"
            :key="day"
            class="date-picker-weekday"
          >
            {{ day }}
          </div>
        </div>
        <div
          class="date-picker-days"
          @mouseleave="hoverDay = null"
        >
          <div
            v-for="i in month.dayOfWeek - 1"
            :key="`empty-${i}`"
          ></div>
          <button
            v-for="day in monthDays(month)"
            :key="day.toString()"
            type="button"
            class="date-picker-day"
            :class="dayClass(day)"
            :disabled="isOutOfRange(day)"
            :aria-pressed="isSelected(day)"
            @mouseenter="hover(day)"
            @focus="hover(day)"
            @click="selectDate(day)"
          >
            <span class="date-picker-day-mark">{{ day.day }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  .date-picker {
    @apply min-w-[280px] rounded-lg p-4 shadow-lg;
    --date-picker-band: color-mix(in srgb, var(--accent) 18%, transparent);
    background-color: var(--surface-overlay);
    border: 1px solid var(--border);
  }

  .date-picker-embedded {
    @apply min-w-0 rounded-none border-0 bg-transparent p-0 shadow-none;
  }

  .date-picker-panes {
    @apply grid gap-x-6;
  }

  .date-picker-header {
    @apply mb-2 flex items-center justify-between;
  }

  .date-picker-month {
    @apply flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-base font-medium capitalize transition-colors;
    color: var(--text-primary);

    &:hover {
      background-color: var(--surface-hover);
    }
  }

  .date-picker-nav-button {
    @apply flex size-8 cursor-pointer items-center justify-center rounded-lg transition-colors;
    color: var(--text-primary);

    &:hover {
      background-color: var(--surface-hover);
    }
  }

  .date-picker-weekdays {
    @apply mb-1 grid grid-cols-7;
  }

  .date-picker-weekday {
    @apply py-1 text-center text-xs font-medium;
    color: var(--text-secondary);
  }

  .date-picker-days {
    @apply grid auto-rows-[2.25rem] grid-cols-7 pointer-coarse:auto-rows-[2.75rem];
  }

  .date-picker-day {
    @apply relative flex cursor-pointer items-center justify-center text-sm tabular-nums;
    color: var(--text-primary);

    &:disabled {
      @apply cursor-not-allowed opacity-40;
    }

    &:hover:not(:disabled) .date-picker-day-mark {
      background-color: var(--surface-hover);
    }
  }

  .date-picker-day-mark {
    @apply relative flex aspect-square w-full max-w-9 items-center justify-center rounded-full transition-colors pointer-coarse:max-w-11;
  }

  .date-picker-day-today .date-picker-day-mark::after,
  .date-picker-month-option.date-picker-day-today::after {
    @apply absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full content-[''];
    background-color: var(--accent);
  }

  .date-picker-band-preview {
    --date-picker-band: color-mix(in srgb, var(--accent) 9%, transparent);
  }

  .date-picker-day-in-range {
    background-color: var(--date-picker-band);
    color: var(--text-primary);
  }

  .date-picker-band-start {
    background: linear-gradient(90deg, transparent 50%, var(--date-picker-band) 50%);
  }

  .date-picker-band-end {
    background: linear-gradient(90deg, var(--date-picker-band) 50%, transparent 50%);
  }

  .date-picker-day-hovered .date-picker-day-mark {
    box-shadow: inset 0 0 0 1px var(--accent);
  }

  .date-picker-day-selected {
    @apply font-medium;
    color: var(--on-accent);

    .date-picker-day-mark {
      background-color: var(--accent);
    }

    &:hover:not(:disabled) .date-picker-day-mark {
      background-color: var(--button-primary-hover);
    }

    .date-picker-day-mark::after {
      background-color: var(--text-primary);
    }
  }

  .date-picker-month-grid {
    @apply grid auto-rows-[3rem] gap-1;
  }

  .date-picker-month-option {
    @apply relative flex cursor-pointer items-center justify-center rounded-lg text-sm capitalize transition-colors;
    color: var(--text-primary);

    &:hover:not(:disabled) {
      background-color: var(--surface-hover);
    }

    &:disabled {
      @apply cursor-not-allowed opacity-40;
    }
  }

  .date-picker-month-option-picked {
    @apply font-medium;
    background-color: var(--accent);
    color: var(--on-accent);

    &:hover:not(:disabled) {
      background-color: var(--button-primary-hover);
    }
  }
}
</style>
